/* GF UI adapters. Shopify/Rise remains authoritative for variants, availability and cart writes. */
(() => {
  if (window.GFCommerce) return;
  const key = 'gf.shopping-size.v1';
  let memorySize = '';
  const preferred = {
    get() { try { return sessionStorage.getItem(key) || memorySize; } catch { return memorySize; } },
    set(value) {
      if (typeof value !== 'string' || !value.trim() || value.length > 80) return;
      memorySize = value;
      try { sessionStorage.setItem(key, value); } catch { /* Private/in-app storage may be unavailable. */ }
      document.dispatchEvent(new CustomEvent('gf:size-changed', { detail: { size: value } }));
    }
  };
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const selectMedia = (items, color, colorOrder = []) => {
    const normalized = normalize(color);
    const explicit = item => item.color === normalized;
    const linked = item => item.color === '-' && item.linked.includes(normalized);
    // Membership is conservative once structured grouping is active.
    const eligible = items.filter(item => explicit(item) || linked(item) || item.color === 'all');
    const image = item => !item.type || item.type === 'image';
    const rank = item => {
      if (!image(item) || item.side === 'lifestyle') return 99;
      if (explicit(item)) {
        if (item.hero) return 0;
        if (item.card === '1') return 1;
        if (['front','back'].includes(item.side)) return 2;
        if (item.side !== 'detail') return 3;
      } else if (linked(item)) return 4;
      return 99;
    };
    // Ordering never changes membership; shared media cannot outrank a color mockup.
    const ranked = eligible.filter(item => rank(item)<99).sort((a,b)=>rank(a)-rank(b));
    const primary = ranked[0] || eligible.find(item=>explicit(item)||linked(item)) || eligible[0];
    const lifestyle = eligible.filter(item=>image(item)&&item.side==='lifestyle').sort((a,b)=>Number(b.card==='2')-Number(a.card==='2'))[0];
    const opposite = primary?.side==='back'?'front':primary?.side==='front'?'back':null;
    const alternate = opposite && eligible.filter(item=>image(item)&&explicit(item)&&item.side===opposite).sort((a,b)=>Number(b.hero)-Number(a.hero))[0];
    const third = alternate || eligible.find(item=>image(item)&&item.card==='3');
    const seen = new Set();
    const full = items.filter(item => item.color !== '-' || item.linked.some(Boolean));
    const effective = item => item.effective || (item.color !== '-' ? item.color : [...new Set(item.linked.filter(Boolean))].length===1 ? item.linked.find(Boolean) : 'linked-shared');
    const group = item => { const index=colorOrder.indexOf(effective(item));return index<0?colorOrder.length:index; };
    full.sort((a,b)=>group(a)-group(b));
    return [primary,lifestyle,third,...full].filter(item=>item && !seen.has(item.id) && seen.add(item.id));
  };
  const recentKey = 'gf.recent-tees.v1';
  let memoryRecent = [];
  const validRecent = item => item && /^\d+$/.test(String(item.id)) && typeof item.handle === 'string' && /^[\p{L}\p{N}_-]{1,180}$/u.test(item.handle);
  const recent = {
    get() {
      try {
        const raw = sessionStorage.getItem(recentKey);
        const parsed = raw ? JSON.parse(raw) : memoryRecent;
        if (!Array.isArray(parsed)) return [];
        const seen = new Set();
        return parsed.filter(item => validRecent(item) && !seen.has(String(item.id)) && seen.add(String(item.id))).slice(0,8);
      } catch { return memoryRecent; }
    },
    add(item) {
      if (!validRecent(item)) return;
      memoryRecent = [{id:String(item.id),handle:item.handle},...recent.get().filter(old => String(old.id)!==String(item.id))].slice(0,8);
      try { sessionStorage.setItem(recentKey,JSON.stringify(memoryRecent)); } catch { /* Session-only memory fallback. */ }
    }
  };
  const trackRecent = () => {
    const product = document.querySelector('product-info[data-gf-recent-handle]');
    if (product) recent.add({id:product.dataset.productId,handle:product.dataset.gfRecentHandle});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',trackRecent,{once:true}); else trackRecent();
  const rankRecommendations = (candidates, size, excluded = []) => {
    const seen = new Set(excluded.map(String));
    return candidates.filter(item => !seen.has(String(item.id)) && seen.add(String(item.id)))
      .map((item,index) => ({item,index})).sort((a,b) => {
        // A recent tee in the remembered size, then other recent tees, then curated pool.
        const tier = item => item.recent ? (item.sizes.includes(size) ? 0 : 1) : 2;
        return tier(a.item)-tier(b.item) || (a.item.recent && b.item.recent ? a.index-b.index : Number(b.item.sizes.includes(size))-Number(a.item.sizes.includes(size)) || a.index-b.index);
      }).slice(0,3).map(entry=>entry.item);
  };
  window.GFCommerce = { preferred, normalize, selectMedia, rankRecommendations, recent };

  customElements.define('gf-v2-carousel', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.track = this.querySelector('[data-gf-track]');
      this.slides = [...this.track.children];
      this.loading = new Map();
      const first = this.slides[0]?.querySelector('img');
      this.slides.slice(1).forEach(slide => slide.classList.add('gf-v31-media-pending'));
      const keepFirstPreview = () => this.slides.slice(1).forEach(slide => {
        // Reuse the browser-selected, already loaded responsive source, not its larger src fallback.
        if (first?.currentSrc && slide.classList.contains('gf-v31-media-pending')) slide.style.backgroundImage = `url(${JSON.stringify(first.currentSrc)})`;
      });
      if (first?.complete) keepFirstPreview();
      else first?.addEventListener('load', keepFirstPreview, {once:true,signal:this.abort.signal});
      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this.nearby = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.hydrate(1); this.nearby.disconnect(); }
        }, { rootMargin: '300px 0px', threshold: 0 });
        this.nearby.observe(this);
      }
      const options = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        const dot = event.target.closest('[data-gf-dot]');
        if (dot) this.go(Number(dot.dataset.gfDot));
        else if (event.target.closest('[data-gf-next]')) this.go(Math.min(this.index()+1,this.slides.length-1));
        else if (event.target.closest('[data-gf-prev]')) this.go(Math.max(this.index()-1,0));
      }, options);
      this.track.addEventListener('scroll', () => {
        if(this.cardDestination===undefined)this.navigation=(this.navigation||0)+1;this.hydrate(this.index());this.syncCardColor();this.prepareNext();
        this.querySelectorAll('[data-gf-dot]').forEach((button,index) => index===this.index() ? button.setAttribute('aria-current','true') : button.removeAttribute('aria-current'));
      }, { ...options, passive: true });
      this.closest('.gf-v2-card')?.addEventListener('click',event=>{if(this.cardPending && event.target.closest('.gf-v2-card__title,.gf-v2-quick-trigger')){event.preventDefault();event.stopImmediatePropagation();}},{...options,capture:true});
      this.track.addEventListener('focusin', event => { const index=this.slides.indexOf(event.target.closest('.gf-v2-carousel__slide')); if(index>=0)this.hydrate(index); }, options);
      const intent = () => this.prepareNext();
      this.addEventListener('pointerenter', intent, options);
      this.addEventListener('focusin', intent, options);
      this.addEventListener('touchstart', event=>{intent();this.cancelCardNavigation();this.touchStart=event.touches[0]?.clientX;}, {...options, passive:true});
      this.addEventListener('touchend',event=>{if(this.touchStart!==undefined && Math.abs((event.changedTouches[0]?.clientX??this.touchStart)-this.touchStart)>8)this.suppressClickUntil=performance.now()+400;this.touchStart=undefined;},{...options,passive:true});
      this.track.addEventListener('pointerdown', event=>{intent();this.cancelCardNavigation();this.dragStart=[event.clientX,event.clientY];}, options);
      this.track.addEventListener('wheel',()=>this.cancelCardNavigation(),{...options,passive:true});
      window.addEventListener('resize',()=>this.cancelCardNavigation(),options);
      this.track.addEventListener('pointerup',event=>{if(this.dragStart && Math.abs(event.clientX-this.dragStart[0])>8)this.suppressClickUntil=performance.now()+400;this.dragStart=null;},options);
      this.addEventListener('click',event=>{if(event.target.closest('.gf-v2-carousel__slide') && (performance.now()<this.suppressClickUntil || this.cardPending)){event.preventDefault();event.stopImmediatePropagation();}},{...options,capture:true});
      queueMicrotask(()=>{if(this.isConnected)this.syncCardColor();});
      this.track.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.go(Math.max(0,Math.min(this.slides.length-1,this.index()+(event.key==='ArrowRight'?1:-1)))); }
      }, options);
    }
    cancelCardNavigation(){this.navigation=(this.navigation||0)+1;this.cardDestination=undefined;this.syncCardColor();}
    setCardPending(pending){
      const trigger=this.closest('.gf-v2-card')?.querySelector('.gf-v2-quick-trigger');
      if(pending && !this.cardPending){this.triggerWasDisabled=!!trigger?.disabled;this.cardPending=true;if(trigger)trigger.disabled=true;}
      else if(!pending && this.cardPending){this.cardPending=false;if(trigger)trigger.disabled=this.triggerWasDisabled;}
    }
    prepareNext(){ const next=this.index()+1;if(next<this.slides.length)this.hydrate(next); }
    syncCardColor(){
      const index=this.index(),slide=this.slides[index],card=this.closest('.gf-v2-card');
      if(!slide || !card)return;
      const trigger=card.querySelector('.gf-v2-quick-trigger');
      if(this.cardDestination!==undefined){
        if(Math.abs(this.track.scrollLeft-this.cardDestination*this.track.clientWidth)>1){this.setCardPending(true);return;}
        this.cardDestination=undefined;
      }
      if(Math.abs(this.track.scrollLeft-index*this.track.clientWidth)>1){this.setCardPending(true);return;}
      const pending=slide.classList.contains('gf-v31-media-pending');
      if(pending){
        this.setCardPending(true);
        this.hydrate(index).then(ok=>{if(ok && this.isConnected && this.index()===index)this.syncCardColor();});
        return;
      }
      this.setCardPending(false);
      if(!slide.dataset.variantId || !slide.dataset.targetColor || slide.dataset.targetColor==='-')return;
      this.dataset.selectedColor=slide.dataset.targetColor;
      this.dataset.selectedVariant=slide.dataset.variantId;
      const base=new URL(this.dataset.productUrl,window.location.origin);
      base.searchParams.set('variant',slide.dataset.variantId);
      base.searchParams.set('gf_color_nav','1');
      card.querySelectorAll('.gf-v2-carousel__slide,.gf-v2-card__title').forEach(link=>{link.href=base.pathname+base.search;});
      if(trigger){base.searchParams.delete('gf_color_nav');base.searchParams.set('view','gf-v2-quick');trigger.dataset.productUrl=base.pathname+base.search;}
    }
    index() { return Math.round(this.track.scrollLeft / (this.track.clientWidth || 1)); }
    hydrate(index) {
      if (this.loading.has(index)) return this.loading.get(index);
      const slide = this.slides[index];
      const template = slide?.querySelector('[data-gf-slide-image]');
      if (template) {
        const fragment = template.content.cloneNode(true);
        fragment.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
        template.replaceWith(fragment);
      }
      const image = slide?.querySelector('img');
      const ready = !image ? Promise.resolve(false) : image.decode ? image.decode().then(() => true).catch(() => false) : new Promise(resolve => {
        if (image.complete && image.naturalWidth) resolve(true);
        else { image.addEventListener('load', () => resolve(true), {once:true}); image.addEventListener('error', () => resolve(false), {once:true}); }
      });
      const result = ready.then(ok => {
        if (ok) { slide.classList.remove('gf-v31-media-pending'); slide.style.backgroundImage = ''; }
        return ok;
      });
      this.loading.set(index,result); return result;
    }
    async go(index) {
      const revision = this.navigation = (this.navigation || 0) + 1;
      this.cardDestination=index;this.syncCardColor();
      const ready=await this.hydrate(index);
      if(revision!==this.navigation || !this.isConnected)return;
      if(!ready){this.cardDestination=undefined;this.syncCardColor();return;}
      this.track.scrollTo({ left: index*this.track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth' });
      this.syncCardColor();this.prepareNext();
    }
    disconnectedCallback() { this.abort?.abort(); this.nearby?.disconnect(); this.navigation = (this.navigation || 0) + 1; }
  });

  customElements.define('gf-v2-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController(); this.root = this.closest('product-info');
      this.track = this.querySelector('[data-gf-gallery-track]');
      this.templates = [...this.querySelectorAll('template[data-gf-gallery-media]')];
      this.sideControl = this.querySelector('[data-gf-side-control]');
      this.sideRevision = 0;
      this.rail=this.querySelector('[data-gf-gallery-thumbs]');
      this.railSlider=this.querySelector('[data-gf-thumbnail-slider]');
      this.railPrev=this.querySelector('[data-gf-rail-prev]');
      this.railNext=this.querySelector('[data-gf-rail-next]');
      this.items = this.templates.map(template => ({ template, id: template.dataset.id, color: template.dataset.color, view: template.dataset.view, side: template.dataset.side, type: template.dataset.mediaType, hero: template.dataset.hero==='1', card: template.dataset.card, effective: template.dataset.effectiveColor, target: template.dataset.targetColor, linked: template.dataset.linkedColors.split('|') }));
      const options = { signal: this.abort.signal };
      this.root.addEventListener('change', event => {
        if (!event.target.closest('variant-selects')) return;
        this.syncColor();
      }, options);
      this.addEventListener('click', event => {
        const thumb=event.target.closest('[data-gf-thumb]');
        if(thumb) { this.sideRevision++; this.goTo(Number(thumb.dataset.gfThumb)); }
        if(event.target.closest('[data-gf-switch-side]')) this.switchSide();
        if(event.target.closest('[data-gf-rail-prev]')) this.scrollThumbnails(-1);
        if(event.target.closest('[data-gf-rail-next]')) this.scrollThumbnails(1);
      }, options);
      this.track.addEventListener('keydown',event=>{
        if(event.target!==this.track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;
        event.preventDefault();this.sideRevision++;
        const current=Math.round(this.track.scrollLeft/(this.track.clientWidth||1)),last=this.track.children.length-1;
        const next=event.key==='Home'?0:event.key==='End'?last:current+(event.key==='ArrowRight'?1:-1);
        this.goTo(Math.max(0,Math.min(last,next)));
      },options);
      this.track.addEventListener('scroll',()=>{this.sideRevision++;this.updateCurrent(true);},{...options,passive:true});
      this.rail.addEventListener('scroll',()=>this.updateRailControls(),{...options,passive:true});
      if(this.railSlider && window.ResizeObserver){
        this.railResize=new ResizeObserver(()=>this.updateRailControls());
        this.railResize.observe(this.railSlider);this.railResize.observe(this.rail);
      }
      window.addEventListener('resize',()=>this.updateRailControls(),options);
      this.render(this.dataset.color);
    }
    updateRailControls() {
      if(!this.railSlider || !this.railPrev || !this.railNext)return;
      // Compare against the full row so arrows cannot keep themselves visible by shrinking the rail.
      const overflow=this.railSlider.clientWidth>0 && this.rail.scrollWidth>this.railSlider.clientWidth+1;
      this.railPrev.hidden=this.railNext.hidden=!overflow;
      const max=Math.max(0,this.rail.scrollWidth-this.rail.clientWidth),left=this.rail.scrollLeft;
      this.railPrev.disabled=!overflow || left<=1;
      this.railNext.disabled=!overflow || left>=max-1;
    }
    scrollThumbnails(direction) {
      this.rail.scrollTo({left:this.rail.scrollLeft+direction*this.rail.clientWidth,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      this.updateRailControls();
    }
    syncColor() {
      const groups=[...this.root.querySelectorAll('variant-selects [data-gf-option]')];
      const group=groups.find(el=>normalize(el.dataset.gfOption)===normalize(this.dataset.colorName)||el.dataset.gfOption==='colour');
      const input=group?.querySelector('input:checked, select');
      if(input && this.followingMedia){this.color=input.value;this.updatePair(input.value);return;}
      if(input) this.render(input.value);
    }
    render(color) {
      if(this.color===color)return;
      this.sideRevision++;
      if(this.sideControl) this.sideControl.hidden=true;
      const items=selectMedia(this.items,color,(this.dataset.colorOrder||'').split(',').filter(Boolean));
      this.updatePair(color);
      // No eligible media: do not retain the previous color or expose another color via fallback.
      this.hidden=!items.length;
      this.dataset.needsFallback='false';this.track.style.visibility='';this.color=color;
      window.pauseAllMedia?.();
      this.activeItems=items;
      this.track.replaceChildren(...items.map(item=>item.template.content.cloneNode(true)));
      const thumbs=items.map((item,index)=>{
        const button=document.createElement('button');button.type='button';button.dataset.gfThumb=String(index);button.dataset.gfMediaId=item.id;const mediaColor=item.effective || item.color;const labelColor=['all','linked-shared','-'].includes(mediaColor)?'':mediaColor.replaceAll('-',' ');const description=item.template.content.querySelector('img')?.alt || '';button.setAttribute('aria-label',`${labelColor} ${item.view || index+1} — ${description}`.trim());button.setAttribute('aria-current',String(index===0));
        const image=document.createElement('img');image.src=item.template.dataset.preview;image.alt='';image.width=64;image.height=72;image.loading='lazy';button.append(image);
        return button;
      });
      this.querySelector('[data-gf-gallery-thumbs]').replaceChildren(...thumbs);
      const primaryImage=this.track.firstElementChild?.querySelector('img');
      if(primaryImage){primaryImage.loading='eager';primaryImage.fetchPriority='high';}
      this.thumbIndex=null;
      this.track.scrollLeft=0;this.updateCurrent();
    }
    updatePair(color) {
      this.pair=null;
      if(this.sideControl) {
        // Explicit color + side only. Variant links/universal images do not prove a pair.
        const matching=this.items.filter(item=>item.color===normalize(color) && item.type==='image');
        const primary=view=>matching.filter(item=>item.side===view).sort((a,b)=>Number(b.hero)-Number(a.hero))[0];
        const front=primary('front'),back=primary('back');
        if(front && back && front.id!==back.id) {
          this.pair={front,back};
        }
      }
    }
    followMediaColor(item) {
      if(!item?.target || item.target==='-' || normalize(this.color)===item.target)return;
      const groups=[...this.root.querySelectorAll('variant-selects [data-gf-option]')];
      const group=groups.find(el=>normalize(el.dataset.gfOption)===normalize(this.dataset.colorName)||el.dataset.gfOption==='colour');
      const option=[...(group?.querySelectorAll('input[type=radio], option')||[])].find(input=>normalize(input.value)===item.target);
      if(!option)return;
      // Same native change event as a swatch. Purchase capture clears stale IDs before Rise fetches.
      this.followingMedia=true;
      try {
        const control=option.tagName==='OPTION'?option.parentElement:option;
        if(option.tagName==='OPTION')control.value=option.value;else option.checked=true;
        control.dispatchEvent(new Event('change',{bubbles:true}));
      } finally { this.followingMedia=false; }
    }
    goTo(index) {
      this.track.scrollTo({left:index*this.track.clientWidth,behavior:'instant'});
      this.updateCurrent(true);
    }
    async switchSide() {
      if(!this.pair || this.sideControl.hidden)return;
      const index=Math.round(this.track.scrollLeft/(this.track.clientWidth||1));
      const current=this.activeItems[index];
      const target=this.pair[current?.side==='front' ? 'back' : 'front'];
      const targetIndex=this.activeItems.indexOf(target), image=this.track.children[targetIndex]?.querySelector('img');
      if(!image)return;
      const revision=++this.sideRevision, color=this.color;
      const button=this.sideControl.querySelector('button');button.disabled=true;
      // Decode just the requested mounted side. Keep the current image in place while loading.
      image.loading='eager';
      try { if(image.decode)await image.decode(); } catch { /* Failed image never replaces a working view. */ return; }
      finally { if(revision===this.sideRevision)button.disabled=false; }
      if(!this.isConnected || revision!==this.sideRevision || color!==this.color)return;
      this.goTo(targetIndex);
    }
    updateCurrent(followColor=false){
      const index=Math.round(this.track.scrollLeft/(this.track.clientWidth||1));
      const thumbnails=this.querySelectorAll('[data-gf-thumb]');
      thumbnails.forEach((b,i)=>b.setAttribute('aria-current',String(i===index)));
      const active=thumbnails[index], rail=this.querySelector('[data-gf-gallery-thumbs]');
      if(active && rail && this.thumbIndex!==index) {
        const itemBox=active.getBoundingClientRect(), railBox=rail.getBoundingClientRect();
        const delta=itemBox.left<railBox.left ? itemBox.left-railBox.left : itemBox.right>railBox.right ? itemBox.right-railBox.right : 0;
        if(index===0)rail.scrollTo({left:0,behavior:'instant'});
        else if(delta)rail.scrollTo({left:rail.scrollLeft+delta,behavior:'instant'});
      }
      this.updateRailControls();
      this.thumbIndex=index;
      const next=this.activeItems?.[index];
      if(followColor && next?.id!==this.dataset.currentMediaId)this.followMediaColor(next);
      this.dataset.currentMediaId=this.activeItems?.[index]?.id || '';
      this.querySelector('[data-gf-gallery-status]').textContent=`${this.color || ''} · ${index+1} / ${this.track.children.length}`;
      if(!this.sideControl)return;
      const current=this.activeItems?.[index];
      const known=current?.type==='image' && current.color===normalize(this.color) && ['front','back'].includes(current.side);
      this.sideControl.hidden=!this.pair || !known || this.dataset.needsFallback==='true';
      if(!known)return;
      if(this.sideControl.hidden)return;
      const other=current.side==='front'?'back':'front', data=this.sideControl.dataset;
      const button=this.sideControl.querySelector('button');
      button.textContent=other==='front'?data.viewFront:data.viewBack;
      button.setAttribute('aria-label',other==='front'?data.frontLabel:data.backLabel);
      button.disabled=false;
    }
    disconnectedCallback(){this.sideRevision++;this.abort?.abort();this.railResize?.disconnect();}

  });

  customElements.define('gf-v2-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.abort=new AbortController();
      this.excluded=(this.dataset.cartProducts || '').split(',').filter(Boolean);
      this.candidates=[...this.querySelectorAll('[data-gf-rec-candidate]')].map(template=>this.candidate(template)).filter(Boolean);
      this.list=this.querySelector('[data-gf-rec-list]');this.render();
      const options={signal:this.abort.signal};
      // Once a shopper interacts, keep ordering stable until the drawer closes.
      this.list.addEventListener('focusin',()=>{this.interacting=true;},options);
      this.list.addEventListener('pointerdown',()=>{this.interacting=true;},options);
      document.addEventListener('gf:size-changed',()=>this.render(),options);
      this.querySelector('[data-gf-change-all]')?.addEventListener('click',()=>{this.interacting=true;this.querySelectorAll('gf-v2-purchase').forEach(control=>control.revealSizes());this.querySelector('gf-v2-purchase')?.focusSize();},options);
      this.drawer=this.closest('cart-drawer');
      if (this.drawer) {
        this.drawerObserver=new MutationObserver(()=>{
          if (this.drawer.classList.contains('active')) this.loadRecent();
          else {this.interacting=false;this.render();}
        });
        this.drawerObserver.observe(this.drawer,{attributes:true,attributeFilter:['class']});
        if(this.drawer.classList.contains('active'))this.loadRecent();
      }
    }
    candidate(template) {
      try {
        const id=template.dataset.productId, node=template.content.querySelector('product-info');
        const sizes=JSON.parse(template.dataset.sizes || '[]');
        const form=node?.querySelector('product-form form');
        if (!/^\d+$/.test(id) || this.excluded.includes(id) || node?.dataset.productId!==id || (!form?.querySelector('[name="id"]')?.value && node?.dataset.gfPersonalizable !== 'true') || !Array.isArray(sizes)) return null;
        return {template,id,sizes:sizes.filter(size=>typeof size==='string')};
      } catch {return null;}
    }
    async loadRecent() {
      if (this.loaded) return;
      this.loaded=true;
      const history=recent.get().filter(item=>!this.excluded.includes(String(item.id))).slice(0,4);
      if(!history.length)return;
      const fetched=await Promise.all(history.map(async item=>{
        // Revalidate through Shopify even if this handle was in the local history yesterday.
        const url=new URL(`${this.dataset.root || '/'}products/${encodeURIComponent(item.handle)}`,location.origin);
        url.searchParams.set('section_id','gf-v32-recommendation');
        const controller=new AbortController();
        const cancel=()=>controller.abort();this.abort.signal.addEventListener('abort',cancel,{once:true});
        const timer=setTimeout(cancel,5000);
        try {
          const response=await fetch(url,{signal:controller.signal,credentials:'same-origin'});
          if(!response.ok || new URL(response.url || url).origin!==location.origin)return null;
          const doc=new DOMParser().parseFromString(await response.text(),'text/html');
          const template=doc.querySelector('template[data-gf-rec-candidate]');
          if(!template || template.dataset.productId!==String(item.id) || template.dataset.handle!==item.handle)return null;
          // Only the compact template is imported. Never execute fetched scripts or inline handlers.
          for(const el of template.content.querySelectorAll('*')) {
            if (el.tagName==='SCRIPT' && el.type!=='application/json')el.remove();
            if (['IFRAME','OBJECT','EMBED','STYLE','LINK'].includes(el.tagName))el.remove();
            for(const attr of [...el.attributes]) {
              if(/^on/i.test(attr.name))el.removeAttribute(attr.name);
              if(['href','src','action','formaction'].includes(attr.name) && !/^https?:$/.test(new URL(attr.value,location.origin).protocol))return null;
            }
          }
          const form=template.content.querySelector('product-form form');
          const action=new URL(form?.getAttribute('action') || '',location.origin);
          if(template.content.querySelector('product-info')?.dataset.gfPersonalizable !== 'true' && (action.origin!==location.origin || !action.pathname.endsWith('/cart/add')))return null;
          const candidate=this.candidate(template);
          return candidate ? {...candidate,recent:true} : null;
        } catch {return null;} finally {clearTimeout(timer);this.abort.signal.removeEventListener('abort',cancel);}
      }));
      if(!this.isConnected || this.abort.signal.aborted)return;
      this.candidates=[...fetched.filter(Boolean),...this.candidates];this.render();
    }
    render() {
      const size=preferred.get();this.querySelector('[data-gf-shopping-size]').textContent=size||'—';
      if(this.interacting)return;
      const selected=rankRecommendations(this.candidates,size,this.excluded);this.hidden=!selected.length;
      const retained=new Map([...this.list.children].map(node=>[node.dataset.productId,node]));
      const nodes=selected.map(item=>retained.get(item.id) || document.importNode(item.template.content.firstElementChild,true));
      [...this.list.children].forEach(node=>{if(!nodes.includes(node))node.remove();});
      nodes.forEach((node,index)=>{if(this.list.children[index]!==node)this.list.insertBefore(node,this.list.children[index]||null);});
    }
    disconnectedCallback(){this.abort?.abort();this.drawerObserver?.disconnect();}
  });

  customElements.define('gf-v2-shopping-state', class extends HTMLElement {
    connectedCallback(){
      this.abort=new AbortController();
      window.addEventListener('scroll',()=>this.offset(),{passive:true,signal:this.abort.signal});
      this.updateVisibility();
      this.unsubscribe=subscribe(PUB_SUB_EVENTS.cartUpdate,()=>this.refresh());
      this.resize=new ResizeObserver(()=>this.offset());const header=document.querySelector('.section-header');if(header)this.resize.observe(header);this.offset();
    }
    offset(){const header=document.querySelector('.section-header');this.style.setProperty('--gf-header-bottom',`${Math.max(0,header?.getBoundingClientRect().bottom||0)}px`);}
    updateVisibility(){this.hidden=Number(this.querySelector('[data-tee-count]')?.dataset.teeCount||0)<1;}
    async refresh(){
      this.controller?.abort();this.controller=new AbortController();
      try {const url=new URL(window.routes.cart_url,location.origin);url.searchParams.set('section_id','gf-v2-shopping-state');const response=await fetch(url,{signal:this.controller.signal});if(!response.ok)return;const html=new DOMParser().parseFromString(await response.text(),'text/html');const state=html.querySelector('.gf-v2-shipping-progress');this.replaceChildren(...(state?[state]:[]));this.updateVisibility();}catch(error){if(error.name!=='AbortError')console.warn('GF shopping state could not refresh');}
    }
    disconnectedCallback(){this.abort?.abort();this.unsubscribe?.();this.controller?.abort();this.resize?.disconnect();}
  });
})();
